import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Badge, Row, Col, Modal, Spinner, ProgressBar } from 'react-bootstrap';
import { FaCheck, FaRocket, FaEye, FaCog, FaGlobe, FaLock, FaUsers, FaPalette, FaCode } from 'react-icons/fa';
import axios from 'axios';
import { API_URL } from '../config';

const WorkflowFinalizer = ({ 
  workflowData,
  executionResults,
  accessibilitySettings,
  onFinalize,
  onClose,
  onPreview
}) => {
  const [finalizationStep, setFinalizationStep] = useState('review'); // 'review', 'configure', 'preview', 'deploy'
  const [toolName, setToolName] = useState('');
  const [toolDescription, setToolDescription] = useState('');
  const [toolUrl, setToolUrl] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState(0);
  const [interfacePreview, setInterfacePreview] = useState(null);
  const [customizations, setCustomizations] = useState({
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    fontFamily: 'inherit',
    customCSS: '',
    showAdvancedOptions: false,
    enableAnalytics: false,
    requireLogin: false
  });

  // Default accessibility prefs baked into the deployed tool (applied on open).
  // Seeded from the builder's current accessibility settings.
  const [accessibilityDefaults, setAccessibilityDefaults] = useState({
    highContrast: !!(accessibilitySettings && accessibilitySettings.highContrast),
    largeFont: !!(accessibilitySettings && (accessibilitySettings.largeFont ?? accessibilitySettings.increasedFontSize)),
    textOnly: !!(accessibilitySettings && accessibilitySettings.textOnly),
  });

  useEffect(() => {
    if (workflowData) {
      // Generate default tool name and description
      const defaultName = workflowData.name || 'Custom Design Tool';
      const defaultDescription = generateDescription();
      setToolName(defaultName);
      setToolDescription(defaultDescription);
      setToolUrl(generateUrlSlug(defaultName));
    }
  }, [workflowData]);

  const generateDescription = () => {
    if (!workflowData) return '';
    
    const mode = workflowData.type || 'workflow';
    const stepCount = workflowData.steps?.length || 0;
    const capabilities = [];
    
    if (executionResults?.filesGenerated > 0) {
      capabilities.push(`generates ${executionResults.filesGenerated} file types`);
    }
    
    if (workflowData.accessibilityFeatures) {
      capabilities.push('accessibility-compliant');
    }
    
    return `A ${mode} design tool with ${stepCount} processing steps that ${capabilities.join(', ')}.`;
  };

  const generateUrlSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const generateInterfacePreview = async () => {
    try {
      // For manual workflows, don't try to generate complex preview - just show a simple message
      if (workflowData.type === 'manual') {
        setInterfacePreview({
          html: `
            <div style='font-family: sans-serif; padding: 20px;'>
              <h3>${toolName || 'Custom Design Tool'}</h3>
              <p>${toolDescription || 'This tool processes your input through a series of agents.'}</p>
              <p><strong>Workflow:</strong></p>
              <ol>
                ${(workflowData.steps || []).map((step, i) => {
                  const agentName = step.agent?.name || step.id || `Agent ${i + 1}`;
                  return `<li>${agentName}</li>`;
                }).join('')}
              </ol>
              <hr/>
              <p style='color: #666;'><em>Users will see a simple input box and get back generated files.</em></p>
            </div>
          `
        });
        return;
      }

      // For automatic workflows, use the backend preview generator
      const response = await axios.post(`${API_URL}/api/generate-interface-preview`, {
        workflow: workflowData,
        accessibility: accessibilitySettings,
        customizations,
        tool_name: toolName,
        tool_description: toolDescription
      });

      if (response.data.success) {
        setInterfacePreview(response.data.preview);
      }
    } catch (error) {
      console.error('Failed to generate interface preview:', error);
      // Fallback to simple preview
      setInterfacePreview({
        html: `<div style='padding: 20px;'><p>Preview unavailable. Deployment will create a functional tool interface.</p></div>`
      });
    }
  };

  const handleDeploy = async () => {
    // Validation
    if (!toolName || toolName.trim() === '') {
      alert('Please enter a tool name before deploying');
      setFinalizationStep('configure');
      return;
    }

    setIsDeploying(true);
    setDeploymentProgress(0);

    try {
      // Check if this is a manual workflow
      const isManualWorkflow = workflowData.type === 'manual';

      if (isManualWorkflow) {
        // Use new manual workflow deployment system
        setDeploymentProgress(20);

        // Step 1: Save workflow to database if not already saved
        const workflowPayload = {
          name: toolName,
          description: toolDescription || 'Custom workflow tool',
          steps: workflowData.steps || [],
          input: workflowData.input || '',
          default_input: workflowData.input || '',
          accessibility_defaults: accessibilityDefaults
        };

        setDeploymentProgress(40);
        console.log('Saving workflow:', workflowPayload);
        const saveResponse = await axios.post(`${API_URL}/api/manual-workflows`, workflowPayload, {
          timeout: 30000, // 30 second timeout
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Save response:', saveResponse.data);
        
        if (!saveResponse.data.success) {
          throw new Error(saveResponse.data.error || 'Failed to save workflow');
        }

        const workflowId = saveResponse.data.workflow_id;
        const deploymentUrl = `${API_URL}/api/tools/${toolUrl || workflowId}`;

        setDeploymentProgress(70);
        console.log('Deploying workflow:', workflowId);

        // Step 2: Deploy the workflow
        const deployResponse = await axios.post(
          `${API_URL}/api/manual-workflows/${workflowId}/deploy`,
          { deployment_url: deploymentUrl },
          {
            timeout: 30000, // 30 second timeout
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('Deploy response:', deployResponse.data);

        if (!deployResponse.data.success) {
          throw new Error(deployResponse.data.error || 'Failed to deploy workflow');
        }

        setDeploymentProgress(100);
        console.log('Deployment successful:', deployResponse.data);

        const finalUrl = deployResponse.data.deployment_url || deploymentUrl;

        // Call the onFinalize callback with deployment info
        onFinalize({
          toolName,
          toolDescription,
          toolUrl: finalUrl,
          deploymentId: workflowId,
          success: true,
          workflowId
        });

      } else {
        // Use old automatic workflow deployment system
        setDeploymentProgress(20);
        await new Promise(resolve => setTimeout(resolve, 500));

        setDeploymentProgress(40);
        await generateInterfacePreview();

        setDeploymentProgress(60);
        const response = await axios.post(`${API_URL}/api/deploy-design-tool`, {
          tool_name: toolName,
          tool_description: toolDescription,
          tool_url: toolUrl,
          workflow: workflowData,
          accessibility: accessibilitySettings,
          customizations,
          execution_results: executionResults
        });

        if (response.data.success) {
          setDeploymentProgress(80);
          setDeploymentProgress(100);
          await new Promise(resolve => setTimeout(resolve, 500));

          onFinalize({
            toolName,
            toolDescription,
            toolUrl: response.data.url,
            deploymentId: response.data.deployment_id,
            success: true
          });
        } else {
          throw new Error(response.data.error || 'Deployment failed');
        }
      }
    } catch (error) {
      console.error('Deployment failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      setDeploymentProgress(0);
      
      let errorMessage = 'Deployment failed: ';
      if (error.code === 'ECONNABORTED') {
        errorMessage += 'Request timed out. The server took too long to respond.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage += 'Network error. Cannot connect to backend server at ${API_URL}';
      } else if (error.response) {
        // Server responded with error
        errorMessage += error.response.data?.error || error.response.statusText || error.message;
      } else if (error.request) {
        // Request made but no response
        errorMessage += 'No response from server. Is the backend running on port 5000?';
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    } finally {
      setIsDeploying(false);
    }
  };

  const renderReviewStep = () => (
    <div className="finalization-step">
      <h5><FaCheck className="me-2" />Review Workflow</h5>
      
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <h6>Workflow Summary</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <strong>Type:</strong> {workflowData.type === 'automatic' ? 'AI-Supervised' : 'Manual Agent Chain'}
              </div>
              <div className="mb-2">
                <strong>Steps:</strong> {workflowData.steps?.length || 0} processing steps
              </div>
              <div className="mb-2">
                <strong>Success Rate:</strong> 
                <Badge bg="success" className="ms-2">
                  {executionResults ? Math.round((executionResults.successful / executionResults.total) * 100) : 0}%
                </Badge>
              </div>
              <div className="mb-2">
                <strong>Avg. Execution Time:</strong> {executionResults?.avgExecutionTime || 0}ms
              </div>
              <div className="mb-2">
                <strong>Generated Files:</strong> {executionResults?.filesGenerated || 0} types
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <h6>Accessibility Features</h6>
            </Card.Header>
            <Card.Body>
              {Object.entries(accessibilitySettings).map(([key, enabled]) => (
                <div key={key} className="mb-2">
                  <Badge bg={enabled ? 'success' : 'secondary'} className="me-2">
                    {enabled ? '✓' : '✗'}
                  </Badge>
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="text-end">
        <Button variant="primary" onClick={() => setFinalizationStep('configure')}>
          Continue to Configuration
        </Button>
      </div>
    </div>
  );

  const renderConfigureStep = () => (
    <div className="finalization-step">
      <h5><FaCog className="me-2" />Configure Design Tool</h5>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Tool Name</Form.Label>
            <Form.Control
              type="text"
              value={toolName}
              onChange={(e) => {
                setToolName(e.target.value);
                setToolUrl(generateUrlSlug(e.target.value));
              }}
              placeholder="My Custom Design Tool"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={toolDescription}
              onChange={(e) => setToolDescription(e.target.value)}
              placeholder="Describe what this tool does..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>URL Slug</Form.Label>
            <div className="input-group">
              <span className="input-group-text">yoursite.com/api/tools/</span>
              <Form.Control
                type="text"
                value={toolUrl}
                onChange={(e) => setToolUrl(e.target.value)}
                placeholder="my-design-tool"
              />
            </div>
          </Form.Group>
        </Col>

        <Col md={6}>
          <h6><FaPalette className="me-2" />Customization</h6>
          
          <Form.Group className="mb-3">
            <Form.Label>Primary Color</Form.Label>
            <Form.Control
              type="color"
              value={customizations.primaryColor}
              onChange={(e) => setCustomizations(prev => ({ ...prev, primaryColor: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Secondary Color</Form.Label>
            <Form.Control
              type="color"
              value={customizations.secondaryColor}
              onChange={(e) => setCustomizations(prev => ({ ...prev, secondaryColor: e.target.value }))}
            />
          </Form.Group>

          <Form.Check
            type="checkbox"
            label="Show Advanced Options to Users"
            checked={customizations.showAdvancedOptions}
            onChange={(e) => setCustomizations(prev => ({ ...prev, showAdvancedOptions: e.target.checked }))}
            className="mb-2"
          />

          <Form.Check
            type="checkbox"
            label="Enable Usage Analytics"
            checked={customizations.enableAnalytics}
            onChange={(e) => setCustomizations(prev => ({ ...prev, enableAnalytics: e.target.checked }))}
            className="mb-2"
          />

          <Form.Check
            type="checkbox"
            label="Require User Login"
            checked={customizations.requireLogin}
            onChange={(e) => setCustomizations(prev => ({ ...prev, requireLogin: e.target.checked }))}
            className="mb-2"
          />

          <fieldset className="mt-3 pt-2 border-top">
            <legend className="h6 fw-normal">Default accessibility for this tool</legend>
            <p className="small text-muted mb-2">
              These are applied automatically when someone opens the deployed tool.
              Users can still change them with the tool's accessibility toolbar.
            </p>
            <Form.Check
              type="checkbox"
              id="a11y-default-contrast"
              label="High contrast by default"
              checked={accessibilityDefaults.highContrast}
              onChange={(e) => setAccessibilityDefaults(prev => ({ ...prev, highContrast: e.target.checked }))}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="a11y-default-largefont"
              label="Large font by default"
              checked={accessibilityDefaults.largeFont}
              onChange={(e) => setAccessibilityDefaults(prev => ({ ...prev, largeFont: e.target.checked }))}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="a11y-default-textonly"
              label="Text-only layout by default"
              checked={accessibilityDefaults.textOnly}
              onChange={(e) => setAccessibilityDefaults(prev => ({ ...prev, textOnly: e.target.checked }))}
              className="mb-2"
            />
          </fieldset>
        </Col>
      </Row>

      <div className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => setFinalizationStep('review')}>
          Back
        </Button>
        <Button variant="primary" onClick={() => { generateInterfacePreview(); setFinalizationStep('preview'); }}>
          Preview Interface
        </Button>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="finalization-step">
      <h5><FaEye className="me-2" />Preview Design Tool</h5>
      
      <div className="interface-preview mb-4">
        {interfacePreview ? (
          <div 
            className="preview-frame"
            style={{
              border: '2px solid #dee2e6',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              minHeight: '400px'
            }}
            dangerouslySetInnerHTML={{ __html: interfacePreview.html }}
          />
        ) : (
          <div className="text-center p-5">
            <Spinner animation="border" />
            <p className="mt-2">Generating interface preview...</p>
          </div>
        )}
      </div>

      <Alert variant="info">
        <strong>Preview Notes:</strong>
        <ul className="mb-0 mt-2">
          <li>This is how your design tool will appear to end users</li>
          <li>All accessibility settings have been applied</li>
          <li>The workflow will be embedded as a simple input/output interface</li>
          <li>Users won't see the complex workflow steps - just the results</li>
        </ul>
      </Alert>

      <div className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => setFinalizationStep('configure')}>
          Back to Configuration
        </Button>
        <Button variant="success" onClick={() => setFinalizationStep('deploy')}>
          Deploy This Tool
        </Button>
      </div>
    </div>
  );

  const renderDeployStep = () => (
    <div className="finalization-step">
      <h5><FaRocket className="me-2" />Deploy Design Tool</h5>
      
      {isDeploying ? (
        <div className="deployment-progress">
          <div className="text-center mb-4">
            <Spinner animation="border" size="lg" />
            <h6 className="mt-3">Deploying {toolName}...</h6>
          </div>
          
          <ProgressBar now={deploymentProgress} className="mb-3" />
          
          <div className="deployment-steps">
            <div className={`step ${deploymentProgress >= 20 ? 'completed' : ''}`}>
              <FaCheck className="me-2" />
              Validating workflow
            </div>
            <div className={`step ${deploymentProgress >= 40 ? 'completed' : ''}`}>
              <FaCode className="me-2" />
              Generating interface
            </div>
            <div className={`step ${deploymentProgress >= 60 ? 'completed' : ''}`}>
              <FaGlobe className="me-2" />
              Creating backend endpoints
            </div>
            <div className={`step ${deploymentProgress >= 100 ? 'completed' : ''}`}>
              <FaRocket className="me-2" />
              Final deployment
            </div>
          </div>
        </div>
      ) : (
        <div className="deployment-ready">
          <Alert variant="warning">
            <h6>Ready to Deploy</h6>
            <p>This will create a live, public design tool that users can access. Make sure you've reviewed everything carefully.</p>
          </Alert>

          <Card className="mb-4">
            <Card.Header>
              <h6>Deployment Summary</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-2"><strong>Tool Name:</strong> {toolName}</div>
              <div className="mb-2"><strong>URL:</strong> yoursite.com/api/tools/{toolUrl}</div>
              <div className="mb-2"><strong>Workflow Type:</strong> {workflowData.type}</div>
              <div className="mb-2"><strong>Accessibility:</strong> {Object.values(accessibilitySettings).filter(Boolean).length} features enabled</div>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-between">
            <Button variant="outline-secondary" onClick={() => setFinalizationStep('preview')}>
              Back to Preview
            </Button>
            <Button variant="danger" onClick={handleDeploy}>
              <FaRocket className="me-2" />
              Deploy Live Tool
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const getStepProgress = () => {
    const steps = ['review', 'configure', 'preview', 'deploy'];
    return steps.indexOf(finalizationStep) + 1;
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaLock className="me-2" />
          Finalize & Deploy Design Tool
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: '500px' }}>
        <style>{`
          .finalization-step {
            margin-bottom: 20px;
          }
          
          .step-progress {
            display: flex;
            justify-content: center;
            margin-bottom: 30px;
          }
          
          .step-indicator {
            display: flex;
            align-items: center;
            margin: 0 10px;
            padding: 10px 20px;
            border-radius: 20px;
            background: #f8f9fa;
            border: 2px solid #dee2e6;
          }
          
          .step-indicator.active {
            background: #007bff;
            color: white;
            border-color: #007bff;
          }
          
          .step-indicator.completed {
            background: #28a745;
            color: white;
            border-color: #28a745;
          }
          
          .deployment-steps .step {
            padding: 10px;
            margin-bottom: 10px;
            border-radius: 5px;
            background: #f8f9fa;
          }
          
          .deployment-steps .step.completed {
            background: #d4edda;
            color: #155724;
          }
          
          .preview-frame {
            transform: scale(0.8);
            transform-origin: top left;
            width: 125%;
            height: 500px;
            overflow: hidden;
          }
        `}</style>

        {/* Step Progress Indicator */}
        <div className="step-progress">
          <div className={`step-indicator ${finalizationStep === 'review' ? 'active' : 'completed'}`}>
            1. Review
          </div>
          <div className={`step-indicator ${finalizationStep === 'configure' ? 'active' : getStepProgress() > 2 ? 'completed' : ''}`}>
            2. Configure
          </div>
          <div className={`step-indicator ${finalizationStep === 'preview' ? 'active' : getStepProgress() > 3 ? 'completed' : ''}`}>
            3. Preview
          </div>
          <div className={`step-indicator ${finalizationStep === 'deploy' ? 'active' : ''}`}>
            4. Deploy
          </div>
        </div>

        {/* Step Content */}
        {finalizationStep === 'review' && renderReviewStep()}
        {finalizationStep === 'configure' && renderConfigureStep()}
        {finalizationStep === 'preview' && renderPreviewStep()}
        {finalizationStep === 'deploy' && renderDeployStep()}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isDeploying}>
          {isDeploying ? 'Deploying...' : 'Cancel'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkflowFinalizer;
